import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import { requestBusinessPrioritiesApplyChanges } from './thunks/requestBusinessPrioritiesApplyChanges'
import { requestBusinessPrioritiesChangeStatus } from './thunks/requestBusinessPrioritiesChangeStatus'
import { requestBusinessPrioritiesData } from './thunks/requestBusinessPrioritiesData'
import { requestBusinessPrioritiesDelete } from './thunks/requestBusinessPrioritiesDelete'
import { requestBusinessPrioritiesPublish } from './thunks/requestBusinessPrioritiesPublish'
import { requestBusinessPrioritiesSetAllMarketsStatus } from './thunks/requestBusinessPrioritiesSetAllMarketsStatus'
import { BusinessPrioritiesViewName, IBusinessPrioritiesState } from './types'

const initialState: IBusinessPrioritiesState = {
  records: [],
  userInfo: null,
  geoMarkets: [],
  isLoading: false,
  error: null,
  editingPriorityId: null,
  activeView: 'list',
  selectedFYear: null,
}

const businessPrioritiesSlice = createSlice({
  name: 'businessPriorities',
  initialState,
  reducers: {
    setActiveView: (state, action: PayloadAction<BusinessPrioritiesViewName>) => {
      state.activeView = action.payload
    },
    setEditingPriority: (state, action: PayloadAction<string | null>) => {
      state.editingPriorityId = action.payload
    },
    fYearSelected: (state, action: PayloadAction<number>) => {
      state.selectedFYear = action.payload
    },
  },
  extraReducers(builder) {
    builder
      .addCase(requestBusinessPrioritiesData.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(requestBusinessPrioritiesData.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.error.message ?? 'Failed to fetch business priorities'
      })
      .addCase(requestBusinessPrioritiesData.fulfilled, (state, action) => {
        state.isLoading = false
        state.records = action.payload.records
        state.userInfo = action.payload.userInfo
        state.geoMarkets = action.payload.geoMarkets
        state.error = null
      })
      .addCase(requestBusinessPrioritiesApplyChanges.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(requestBusinessPrioritiesApplyChanges.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.error.message ?? 'Failed to apply business priorities changes'
      })
      .addCase(requestBusinessPrioritiesApplyChanges.fulfilled, (state, action) => {
        const updatedRecord = action.payload
        state.isLoading = false
        const index = state.records.findIndex((record) => record.uid === updatedRecord.uid)
        if (index !== -1) {
          state.records[index] = updatedRecord
        } else {
          state.records.push(updatedRecord)
        }
        state.error = null
      })
      .addCase(requestBusinessPrioritiesChangeStatus.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(requestBusinessPrioritiesChangeStatus.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.error.message ?? 'Failed to change business priorities status'
      })
      .addCase(requestBusinessPrioritiesChangeStatus.fulfilled, (state, action) => {
        const updatedRecord = action.payload
        state.isLoading = false
        const index = state.records.findIndex((record) => record.uid === updatedRecord.uid)
        if (index !== -1) {
          state.records[index] = updatedRecord
        }
        state.error = null
      })
      .addCase(requestBusinessPrioritiesSetAllMarketsStatus.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(requestBusinessPrioritiesSetAllMarketsStatus.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.error.message ?? 'Failed to change business priorities status'
      })
      .addCase(requestBusinessPrioritiesSetAllMarketsStatus.fulfilled, (state, action) => {
        const updatedRecord = action.payload
        state.isLoading = false
        const index = state.records.findIndex((record) => record.uid === updatedRecord.uid)
        if (index !== -1) {
          state.records[index] = updatedRecord
        }
        state.error = null
      })
      .addCase(requestBusinessPrioritiesDelete.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(requestBusinessPrioritiesDelete.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.error.message ?? 'Failed to delete business priorities'
      })
      .addCase(requestBusinessPrioritiesDelete.fulfilled, (state, action) => {
        state.isLoading = false
        const deletedRecord = action.payload
        const index = state.records.findIndex((record) => record.uid === deletedRecord.uid)
        if (index !== -1) {
          state.records.splice(index, 1)
        }
        state.error = null
      })
      .addCase(requestBusinessPrioritiesPublish.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(requestBusinessPrioritiesPublish.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.error.message ?? 'Failed to publish business priorities'
      })
      .addCase(requestBusinessPrioritiesPublish.fulfilled, (state) => {
        state.isLoading = false
        if (state.userInfo) {
          state.userInfo.availableActions = state.userInfo.availableActions.filter(
            (action) => action !== 'publish'
          )
        }
        state.error = null
      })
  },
})

export const { setActiveView, setEditingPriority, fYearSelected } = businessPrioritiesSlice.actions
export const businessPrioritiesReducer = businessPrioritiesSlice.reducer
